import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of, switchMap, throwError } from 'rxjs';
import { RequestResponse } from '@core/services/request/model/request.model';
import { environment } from '@env/environment';
import { catchError } from 'rxjs/operators';
import { inject } from '@angular/core';
import { FloatingAlertsService } from '@shared/components/floating-alerts/data/floating-alerts.service';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';

/**
 * Token could be true, false or undefined
 * - If it's true, the error alert will not show up
 * - If it's false or undefined, error alert will show up
 */
export const HIDE_ERROR_ALERT_CXT_TOKEN = new HttpContextToken<boolean | undefined>(() => false);

function buildHttpErrorResponse(httpEvent: HttpResponse<unknown>, response: RequestResponse<unknown>) {
  const error = response.info != null && response.info.length > 0 ? response.info.map((value) => value.message) : [response.message];
  return throwError(
    () =>
      new HttpErrorResponse({
        error,
        headers: httpEvent.headers,
        status: httpEvent.status,
        statusText: httpEvent.statusText,
        url: httpEvent.url ?? undefined,
      })
  );
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const floatingAlertsService = inject(FloatingAlertsService);
  return next(req).pipe(
    switchMap((httpEvent) => {
      if (
        httpEvent instanceof HttpResponse &&
        httpEvent.url != null &&
        httpEvent.url.indexOf(environment.baseUrl) != -1 &&
        httpEvent.body != null &&
        typeof httpEvent.body === 'object'
      ) {
        const body = httpEvent.body;
        if (body instanceof Blob && body.type === 'application/json') {
          return fromPromise(body.text()).pipe(
            switchMap((bodyText) => buildHttpErrorResponse(httpEvent, JSON.parse(bodyText) as RequestResponse<unknown>))
          );
        }
        if ('success' in body && !body.success) {
          return buildHttpErrorResponse(httpEvent, body as RequestResponse<unknown>);
        }
      }
      return of(httpEvent);
    }),
    catchError((err: HttpErrorResponse) => {
      if (req.context.get(HIDE_ERROR_ALERT_CXT_TOKEN)) {
        throw err;
      }
      if (Array.isArray(err.error) && err.error.every((value) => typeof value === 'string')) {
        err.error.forEach((value) => floatingAlertsService.error(value));
      } else if (typeof err.error === 'string') {
        floatingAlertsService.error(err.error);
      } else if (err.message) {
        floatingAlertsService.error(err.message);
      }
      throw err;
    })
  );
};
